import { z } from 'zod'

import { pruned } from '@/lib/filters'

/**
 * The URL state of Manage Focused SKUs: the scope, which tab is open, the
 * list's search and page, and the search for listings to add. Ticked rows
 * are not here — a selection is work in progress, not a view.
 *
 * Optional with `.catch` throughout; defaults applied at read time and
 * pruned on the way out, so the bare URL is the default view.
 */

export const MANAGE_PAGE_SIZES = [25, 50, 100] as const
export const DEFAULT_MANAGE_PAGE_SIZE = 50

export const manageSearchSchema = z.object({
  warehouse: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe(
      'The warehouse whose linked stores are offered, by id. Changing it clears the store.',
    ),
  store: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe(
      'The store whose listings are shown and focused, by id. Nothing loads until a warehouse and a store are chosen.',
    ),
  /** Brand ids; the lists and counts are narrowed to them. */
  brands: z
    .array(z.number().int().positive())
    .optional()
    .catch(undefined)
    .describe('Brands to narrow the lists and counts to, by id. Empty means every brand.'),
  /** `list`: the person's focused SKUs; `add`: a store's listings to add. */
  tab: z
    .enum(['list', 'add'])
    .optional()
    .catch(undefined)
    .describe(
      'The tab: list shows the focused SKUs, add the listings not focused yet. Default list.',
    ),
  q: z
    .string()
    .max(200)
    .optional()
    .catch(undefined)
    .describe('Search over the Focused tab by ASIN, SKU, seller SKU or title.'),
  page: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('The page of the list. Default 1.'),
  limit: z
    .union([z.literal(25), z.literal(50), z.literal(100)])
    .optional()
    .catch(undefined)
    .describe('Rows per page. Default 50.'),
  /** The search over the store's listings, on the Add tab. */
  find: z
    .string()
    .max(200)
    .optional()
    .catch(undefined)
    .describe('Search over the Not focused tab by ASIN, SKU, seller SKU or title.'),
})

export type ManageSearch = z.infer<typeof manageSearchSchema>

export function withoutManageDefaults(search: ManageSearch): ManageSearch {
  return pruned({
    ...search,
    tab: search.tab === 'list' ? undefined : search.tab,
    limit: search.limit === DEFAULT_MANAGE_PAGE_SIZE ? undefined : search.limit,
  })
}
